import { useQuery } from '@tanstack/react-query'
import { getRegistry } from '../services/registryApi'
import type { RegistryQuery } from '../types/registry'
import { errorKind } from '../utils/format'

/**
 * No retry on a refusal that a second try cannot change: a 403 (the caller lacks
 * Lighthouse.Admin), a 401, and a 400 (an unknown status or workspace). Retrying
 * one only delays the state the reader should see. Anything else gets the app's
 * single retry (main.tsx).
 */
export function shouldRetryRegistry(failureCount: number, error: unknown): boolean {
  const kind = errorKind(error)
  if (kind === 'forbidden' || kind === 'unauthorized' || kind === 'bad-request') return false
  return failureCount < 1
}

/**
 * One page of the Report Registry.
 *
 * NO refetchInterval, for the reason useConnections gives (hooks/useOverview.ts):
 * nothing on this surface moves on a clock a poll could keep up with. A report is
 * created, filed, transferred or archived when a person does it in Cartographer,
 * and the nightly jobs that touch reports (the orphan check) run once a day.
 * What a poll would cost is real — every distinct filter is its own cache entry
 * with its own timer, and each call runs two COUNTs — so a page offers an
 * explicit refresh instead.
 *
 * The query is part of the key by value, and `getRegistry` is called through a
 * closure, so the key and the argument move together and a filter change can never
 * serve the previous filter's cached page.
 *
 * Takes `enabled` so a page can honour the gate contract (hooks/useLighthouseAdmin.ts):
 * on `denied`, render RestrictedState without fetching an endpoint the caller
 * cannot read; on `unknown`, fetch, and render the endpoint's own 403 as restricted.
 */
export function useRegistry(
  query: RegistryQuery,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['observatory', 'registry', query],
    queryFn: () => getRegistry(query),
    enabled,
    retry: shouldRetryRegistry,
  })
}
